import { Form } from 'react-final-form';
import { useState, useMemo, FormEvent, useCallback } from 'react';
import { Button } from '@oms/ui-button';
import { Stack } from '@oms/ui-stack';
import { useValidate } from './validate';
import { FORM_ERROR } from 'final-form';
import { Notification } from '@oms/ui-notification';
import { post, put } from 'utils/fetch';
import { useItemSector } from 'context/ItemSectorContext';
import { SellForm } from './SellForm';
import { TradeUtil } from 'utils/trade';
import { Checkout } from '../Checkout';
import { Box } from '@oms/ui-box';
import { normalizeNumber } from 'utils/form';
import { useSelectedAccountId } from 'context/SelectedAccountContext';
import { useSelectedAccount } from 'utils/hooks/useAccounts';
import { useAllActiveOrders } from 'utils/hooks/useActiveOrders';
import { getMatchingOrder } from 'utils/validation/getMatchingOrder';
import { useUser } from 'utils/hooks/useUser';
import moment from 'moment';
import { ORDERS_URL } from 'constants/Jaws';
import { fetchError } from 'utils/validation/fetchError';
import useHoldings from 'utils/hooks/useHoldings';
import { InstrumentOverview } from '../InstrumentOverview';
import { OrderDepth as OMSOrderDepth } from '@oms/components-order-depth';
import { useMedia } from '@oms/ui-media';
import { useQueryClient } from 'react-query';
import { TradeSuccessMessage } from '../TradeSuccessMessage';
import useIsStatusU from 'utils/hooks/useIsStatusU';

export type Account = {
  label: string;
  value: string;
  accountNo: number;
};

export type Trade = {
  limit?: number;
  last?: number;
  volume?: number;
  tradeType: 'SELL';
  expirationDate?: any;
  useStopLoss: boolean;
  useOpenVolume: boolean;
  visible?: boolean;
  account?: Account;
  ticker: string;
  exchange: string;
};

export type ProBrokerResponse = {
  ACTIVE_SALES_ORDERS: number;
  AVAIL_POSITION: number;
  BACKEND_ID: 'probroker' | string;
  CUSTOMER_ID: string | number;
  ERROR_CODE: number;
  ERROR_MESSAGE: string;
  ORDER_ID: string | number;
  ORDER_STATUS: number | 'M' | 'B';
  REQUST_REFERENCE: string;
  SF_CAPITAL: number;
  SF_COLLATERAL: number;
  SF_EQUITY: number;
  SF_EXPOSURE: number;
};

export type ProBrokerResponseObject = {
  data: ProBrokerResponse;
};

export const getItemAndSector = (itemSectorValue: any) => {
  const [item = '', useSector = ''] = itemSectorValue?.split('.');
  return {
    item,
    useSector,
  };
};

export const Sell = ({
  initialValues: initialValuesFromProps,
  orderId,
  onSubmitCompleted,
  includeInstrumentOverview = false,
}: any) => {
  const queryClient = useQueryClient();
  const isEdit = !!orderId;
  const itemSectorValue = useItemSector();

  const {
    data: { userId = '', hasLevel2 },
  } = useUser();
  const [showCheckout, setShowCheckout] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [start] = useState(TradeUtil.getDefault('start'));

  const { selectedAccountId } = useSelectedAccountId();
  const activeOrders = useAllActiveOrders(userId);
  const {
    data: { selectedAccount },
  } = useSelectedAccount();
  const isAsk = selectedAccount?.ask;
  const isMargin = selectedAccount?.isMarginAccount;
  const accountId = selectedAccountId;
  const { data } = useHoldings({ accountId });
  const { isMobile } = useMedia();
  const isBlocked = useIsStatusU();

  const initialValues = useMemo(
    () => ({
      tradeType: 'SELL',
      useOpenVolume: false,
      useStopLoss: false,
      account: selectedAccount,
      expirationDate: start,
      ticker: itemSectorValue
        ? getItemAndSector(itemSectorValue).item
        : undefined,
      exchange: itemSectorValue
        ? getItemAndSector(itemSectorValue).useSector
        : undefined,
      ...initialValuesFromProps,
    }),
    [initialValuesFromProps, itemSectorValue, selectedAccount, start],
  );

  const handleSuccessMessageClose = useCallback(
    form => {
      form.restart();
      setShowOrderConfirmation(false);
      setShowCheckout(false);
    },
    [setShowOrderConfirmation],
  );

  const validate = useValidate();
  if (!itemSectorValue) return null;
  const { item } = getItemAndSector(itemSectorValue);

  const holdings = data?.rows.find(
    item => item.ITEM_SECTOR === itemSectorValue,
  );

  const submit = async (values: any, form: any, onErrorCallback: any) => {
    const {
      tradeType,
      volume,
      limit,
      useOpenVolume,
      openVolume,
      useStopLoss,
      stopLossLimit,
      ticker,
      expirationDate,
      exchange,
    } = values;

    const normalizedLimit = normalizeNumber(limit);
    const normalizedVolume = normalizeNumber(volume);
    const normalizedOpenVolume = normalizeNumber(openVolume);

    // validation rule, should not be a part of submit
    if (!!getMatchingOrder(tradeType, item, limit, activeOrders)) {
      return {
        [FORM_ERROR]:
          'Du har en kryssende ordre i dette instrumentet som gjør at du ' +
          'risikerer å handle med deg selv. Vennligst endre limit for å ' +
          'unngå dette. ',
      };
    }

    const payload = {
      data: {
        accountId: selectedAccountId,
        tradeType,
        ticker,
        exchange,
        expirationDate: moment(expirationDate).format('YYYY-MM-DD'),
        volume: normalizedVolume,
        useOpenVolume: !!useOpenVolume,
        openVolume: useOpenVolume ? normalizedOpenVolume : null,
        useTriggerCriterion: useStopLoss,
        stopLossLimit: useStopLoss ? normalizeNumber(stopLossLimit) : undefined,
        limit: normalizedLimit,
        orderType: 'normal',
      },
    };

    try {
      let response: Response;
      if (isEdit) {
        response = await put(
          `${ORDERS_URL}/${userId}/orders/${orderId}/execute`,
          JSON.stringify(payload),
        );
      } else {
        response = await post(
          `${ORDERS_URL}/${userId}/orders/execute`,
          JSON.stringify(payload),
        );
      }
      if (!response.ok) {
        fetchError(
          response,
          onErrorCallback,
          tradeType,
          isAsk,
          isMargin,
          FORM_ERROR,
        );
        return;
      }

      queryClient.invalidateQueries('activeOrders');
      setShowOrderConfirmation(true);
      onSubmitCompleted?.();
    } catch (error) {
      onErrorCallback({
        [FORM_ERROR]: 'Noe gikk galt ved innsending',
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={submit} validate={validate}>
      {({
        handleSubmit,
        submitting,
        submitError,
        values,
        form,
        dirtySinceLastSubmit,
        hasValidationErrors,
      }) => {
        const { tradeType, limit, ticker } = values;
        const matchingOrder = getMatchingOrder(
          tradeType,
          ticker,
          limit,
          activeOrders,
        );

        return (
          <Stack
            as="form"
            gap={5}
            p={2}
            onSubmit={(e: FormEvent) => e.preventDefault()} // Prevent submit on enter
            width="100%"
          >
            {(isMobile || includeInstrumentOverview) && (
              <>
                <InstrumentOverview
                  itemSector={itemSectorValue}
                  holdings={holdings?.POSITION_VOLUME}
                />
                <OMSOrderDepth
                  itemSector={itemSectorValue}
                  spec={
                    hasLevel2
                      ? {
                          convert: 'levels',
                          dataSet: 'orders',
                          limit: 5,
                        }
                      : undefined
                  }
                />
              </>
            )}
            <>
              {submitError && (
                <Notification status="error" title="Feil">
                  {submitError}
                </Notification>
              )}
            </>
            <SellForm
              editMode={isEdit}
              hidden={showCheckout || showOrderConfirmation}
              holdings={holdings?.POSITION_VOLUME}
              sell={
                <Button
                  type="button"
                  title={isEdit ? 'Endre' : 'Selg'}
                  disabled={hasValidationErrors || isBlocked}
                  onClick={() => {
                    // Guard
                    if (!hasValidationErrors) {
                      setShowCheckout(true);
                    }
                  }}
                >
                  {isEdit ? 'Endre' : 'Selg'}
                </Button>
              }
            />
            <Box>
              {isAsk && (
                <p>
                  Merk at du nå handler på din Aksjeparekonto. Din beholdning i
                  aksjer registrert på din aksjesparekonto vil du finne på
                  Aksjesparekontosidene dine både i Aksjehandelsløsningen og i
                  nett- og mobilbanken. Vær oppmerksom på at det vil være et
                  døgns forsinkelse på markedsverdien som vises i
                  beholdningsoversikten i nett- og mobilbanken. I aksjetjenesten
                  vil det ikke være tilsvarende forsinkelse.
                </p>
              )}
            </Box>

            <Checkout
              hideActions={showOrderConfirmation}
              matchingOrder={matchingOrder}
              accountId={selectedAccountId}
              ticker={ticker}
              userId={userId}
              hidden={!showCheckout}
              values={values}
              back={
                <Button
                  type="button"
                  title="Tilbake"
                  variant="secondary"
                  onClick={() => {
                    setShowCheckout(false);
                    const { values } = form.getState();
                    form.restart(values);
                  }}
                  disabled={submitting}
                >
                  Tilbake
                </Button>
              }
              submit={
                !matchingOrder && (
                  <Button
                    title={'Bekreft'}
                    isPending={submitting}
                    disabled={submitting}
                    onClick={handleSubmit}
                  >
                    Bekreft
                  </Button>
                )
              }
              error={(!dirtySinceLastSubmit && submitError) || null}
            />
            <TradeSuccessMessage
              orderType={'SELL'}
              isHidden={!showOrderConfirmation}
              isEdit={isEdit}
              ticker={values.ticker}
              expirationDate={values.expirationDate}
              onClose={() => handleSuccessMessageClose(form)}
            />
          </Stack>
        );
      }}
    </Form>
  );
};
